import * as React from "react";
import { formatMessage } from "lib/globalize-util";
import { AlertDialog } from "components/dialog/alert";
import Button from "components/form/button";
import { DATATEST } from "constants/integration";
import { IDoorTotal } from "reducers/collection";
import { IGridData } from "helpers/collection";
import { get } from "lodash";
import { fromJS, List, Set } from "immutable";
import { getSkuOrId } from "helpers/product-collection";
import { TextField } from "../product-collection-views/product-collection-item-modal/text-field";
import { merge, $ } from "glamor";
import theme from "styles/theme";
import ErrorOutlineOutlinedIcon from "@material-ui/icons/ErrorOutlineOutlined";
import { PoiTag } from "constants/product-collection";
import { formatPercentageOrCurrency } from "../footer/components/";

interface IProps {
    onCapture: (id) => void;
    onRequestClose: () => void;
    tileViewData?: IGridData;
    doorTotals: {
        [door: string]: IDoorTotal;
    };
    selectedVersionStatus: string;
    uniqueBrandEntities?: any[];
    poiTags: List<any>;
}

const muiStyles = {
    button: {
        cancel: {
            marginRight: "4px",
        },
        capture: {
            marginLeft: "4px",
        },
    },
    buttons: {
        display: "flex",
        justifyContent: "flex-end",
        marginTop: "50px",
    },
};
export const CaptureSizeBulksDialog: React.FC<IProps> = ({
    onCapture,
    doorTotals,
    tileViewData,
    selectedVersionStatus,
    onRequestClose,
    uniqueBrandEntities,
    poiTags,
}) => {
    const styles = React.useMemo(
        () =>
            merge(
                $(" .fieldLabel", {
                    flexGrow: "1",
                    maxWidth: "190px",
                    minWidth: "190px",
                    padding: "10px 12px",
                    backgroundColor: theme.palette.lightestGrey,
                    color: theme.palette.darkGrey,
                    fontSize: "14px",
                    fontWeight: "700",
                }),
                $(" .fieldContainer", {
                    display: "flex",
                }),
                $(" p.message", {
                    padding: "0 0 20px",
                }),
                $(" p.message:first-child", {
                    padding: "20px 0",
                }),
                $(" p.message strong", {
                    color: theme.palette.darkestGrey,
                    fontWeight: 900,
                })
            ),
        []
    );

    const overridingBulks: boolean = React.useMemo(
        () => !!poiTags.find((tag) => tag.get(PoiTag.COLLECTION_VERSION_STATUS_ID) === selectedVersionStatus),
        [poiTags, selectedVersionStatus]
    );

    const totals = React.useMemo(() => {
        const retail = get(doorTotals, ["_total", "retail"]);
        const wholesale = get(doorTotals, ["_total", "wholesale"]);
        const percentageValue = ((retail - wholesale) / retail) * 100;
        return {
            uniqueItemCount: { display_name: "Total Products", value: Set(tileViewData?.data?.map(getSkuOrId)).size },
            totalItemCount: { display_name: "Total Rows", value: tileViewData?.data?.size },
            quantity: {
                display_name: "Total Units",
                value: formatPercentageOrCurrency(get(doorTotals, ["_total", "units"]), false, undefined, false),
            },
            wholesale: {
                display_name: "Total Cost",
                value: formatPercentageOrCurrency(wholesale, true, undefined, false),
            },
            retail: { display_name: "Total Retail", value: formatPercentageOrCurrency(retail, true, undefined, false) },
            markup: {
                display_name: "Total Markup",
                value: formatPercentageOrCurrency(retail > 0 ? percentageValue : 0, false, undefined, true),
            },
        };
    }, [doorTotals]); // eslint-disable-line react-hooks/exhaustive-deps

    const titleStyle = React.useMemo(() => ({ display: "flex", alignItems: "center" }), []);
    const iconStyle = React.useMemo(() => ({ width: "24px", height: "24px", margin: "0 5px" }), []);
    const unusedCallback = React.useCallback(() => {}, []);
    const emptySet = React.useMemo(() => Set<string>(), []);

    const handleCapture = React.useCallback(() => {
        !!selectedVersionStatus && onCapture(selectedVersionStatus);
        onRequestClose();
    }, [selectedVersionStatus, onCapture, onRequestClose]);

    return (
        <AlertDialog
            dismissButton={false}
            onRequestClose={onRequestClose}
            open={!!selectedVersionStatus}
            title={
                <div>
                    <ErrorOutlineOutlinedIcon style={iconStyle} htmlColor={theme.palette.errorAlertTextColor} />
                    {formatMessage(`Are you sure${overridingBulks ? " you want to override your bulks" : ""}?`)}
                </div>
            }
            titleStyle={titleStyle}
            contentStyle={{ maxWidth: "600px" }}
        >
            <div className="assortmentInfo" {...styles}>
                <p className="message">
                    {formatMessage(
                        overridingBulks
                            ? "You already have an existing version of this assortment tagged with this status. Performing this action will remove this tag from the previous version and apply it to the current version of this assortment, overriding the previously captured size bulks. Are you sure you want to continue?"
                            : "Performing this action will capture the bulk units per size for each product within this assortment as they exist right now. This version of the assortment will be tagged with the status that you’ve selected. Please review the below assortment summary before moving forward."
                    )}
                </p>
                {!!uniqueBrandEntities?.length && (
                    <p className="message">
                        <strong>{formatMessage(`Brand${uniqueBrandEntities.length > 1 ? "s" : ""}`)}: </strong>
                        {uniqueBrandEntities
                            .map((brandEntity) => brandEntity?.name)
                            .filter((name) => !!name)
                            .join(", ")}
                        .
                    </p>
                )}
                {Object.entries(totals).map(([key, totalObject]) => (
                    <TextField
                        key={key}
                        value={`${totalObject.value}`}
                        field={fromJS({ display_name: totalObject.display_name })}
                        readOnly={true}
                        bindEditor={unusedCallback}
                        hashtags={emptySet}
                        onKeyDown={unusedCallback}
                        onChange={unusedCallback}
                    />
                ))}
            </div>
            <footer style={muiStyles.buttons}>
                <Button
                    label={formatMessage("Close")}
                    onClick={onRequestClose}
                    style={muiStyles.button.cancel}
                    data-test={DATATEST.ASSORTMENTS.CLOSE_CAPTURE_BTN}
                />
                <Button
                    label={formatMessage(overridingBulks ? "Yes, capture and override" : "Capture")}
                    primary={true}
                    onClick={handleCapture}
                    style={muiStyles.button.capture}
                    data-test={DATATEST.ASSORTMENTS.CAPTURE_BULKS_BTN}
                />
            </footer>
        </AlertDialog>
    );
};
